import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { FreeMode, Mousewheel } from 'swiper/modules';
import type { Swiper } from 'swiper/types';
import type { UrlObject } from 'url';
import { type Analytics, getFilterToolbarAnalyticsEvents, useSportSearchInput, useSportUrlParams } from '@mwl/core-lib';
import { NoSSR } from '@mwl/ui';
import ArrowRightIcon from '@public/assets/common/icons/arrowRightOutlined.svg';
import CalendarIcon from '@public/assets/sportToolbar/icons/calendar.svg';
import LiveIcon from '@public/assets/sportToolbar/icons/live.svg';
import PregameIcon from '@public/assets/sportToolbar/icons/pregame.svg';
import type { ButtonAsLink } from '@/components/Button/Button';
import { Button } from '@/components/Button/Button';
import { ButtonIcon } from '@/components/ButtonIcon/ButtonIcon';
import { Slider } from '@/components/Slider/Slider';
import { SportIcon } from '@/components/SportIcon/SportIcon';
import { routes } from '@/constants';
import { useIsCyberSportPage } from '@/hooks';
import { SportSearchInput } from '../components/SportSearchInput/SportSearchInput';
import { getSportToolbarAnalytics } from '../SportToolbar.analytics';
import type { DesktopSportToolbarProps } from './DesktopSportToolbar.types';
import styles from './DesktopSportToolbar.module.scss';
const commonButtonProps: Omit<ButtonAsLink, 'href'> = {
  as: 'link',
  color: 'grey',
  size: 'sm',
  variant: 'text'
};
export const DesktopSportToolbar: FC<DesktopSportToolbarProps> = ({
  className,
  sports
}) => {
  const {
    t
  } = useTranslation('sport', {
    keyPrefix: 'filters'
  });
  const [swiper, setSwiper] = useState<Swiper>();
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
  const prevRef = useRef<HTMLDivElement | null>(null);
  const nextRef = useRef<HTMLDivElement | null>(null);
  const isCyberSportPage = useIsCyberSportPage();
  const {
    sportIds,
    stage,
    search,
    createSportItemLink,
    createStageLink,
    patchParams
  } = useSportUrlParams({
    isCyberSportPage,
    routes
  });
  const getAnalyticsData = useCallback((href: UrlObject, position: number, isActive: boolean): Analytics<'click'> => {
    const {
      sportToolbarAnalytics
    } = getSportToolbarAnalytics({
      href,
      count: sports?.length,
      position
    });
    if (isActive) {
      return {
        click: {
          eventName: sportToolbarAnalytics.unselect?.eventName,
          data: sportToolbarAnalytics.unselect?.data
        }
      };
    }
    return {
      click: {
        eventName: sportToolbarAnalytics.select?.eventName,
        data: sportToolbarAnalytics.select?.data
      }
    };
  }, [sports]);
  useEffect(() => {
    if (!swiper) {
      return;
    }
    const {
      navigation
    } = swiper.params;
    if (typeof navigation === 'boolean' || navigation == null) {
      return;
    }
    navigation.prevEl = prevRef.current;
    navigation.nextEl = nextRef.current;
    navigation.disabledClass = styles.disabledNav;
    swiper.navigation.init();
  }, [swiper]);
  useEffect(() => {
    swiper?.navigation?.update();
    setIsNextDisabled(nextRef.current?.classList.contains(styles.disabledNav) ?? false);
  }, [swiper, sports]);
  const searchRef = useRef<HTMLDivElement>(null);
  const {
    searchViewState,
    ...params
  } = useSportSearchInput({
    elementRef: searchRef,
    routes
  });
  const pregameButton = {
    text: t('pregame', 'Pregame'),
    href: createStageLink('PREGAME'),
    sectionName: 'Pregame',
    isActive: stage === 'PREGAME'
  };
  const liveButton = {
    text: t('live', 'Live'),
    href: createStageLink('LIVE'),
    sectionName: 'Live',
    isActive: stage === 'LIVE'
  };
  return <div className={cn(styles.root, className, {
    [styles.activeSearch]: searchViewState !== 'default' || search
  })} data-sentry-component="DesktopSportToolbar" data-sentry-source-file="DesktopSportToolbar.tsx">
      <SportSearchInput className={styles.search} searchRef={searchRef} searchViewState={searchViewState} {...params} data-sentry-element="SportSearchInput" data-sentry-source-file="DesktopSportToolbar.tsx" />

      {!sports.length ? <div className={styles.gutter} /> : <div className={styles.sliderWrapper}>
          <Button {...commonButtonProps} className={cn(styles.button, {
        [styles.activeButton]: sportIds === 'all' && !stage
      })} href={{
        query: patchParams({
          sps: (stage || sportIds === 'all') && !isCyberSportPage ? [] : 'all',
          sp: '',
          ch: '',
          chs: [],
          ct: '',
          cts: []
        })
      }} shallow color={sportIds === 'all' ? 'red' : 'grey-light'} variant={sportIds === 'all' ? 'filled' : 'text'} skew="right">
            {t('all', 'All')}
          </Button>

          <div className={styles.navWrapper}>
            <div ref={prevRef} className={styles.navLeft}>
              <ButtonIcon as="button" variant="text" size="auto" aria-label="sport toolbar prev">
                <ArrowRightIcon width={20} height={20} />
              </ButtonIcon>
            </div>
            <Slider classes={{
          wrapper: styles.slider,
          endShadow: styles.sliderEndShadow,
          startShadow: styles.sliderStartShadow
        }} onSwiper={setSwiper} slidesPerView="auto" spaceBetween={16} isEndShadow={!isNextDisabled} isStartShadow modules={[Mousewheel, FreeMode]} freeMode mousewheel={{
          forceToAxis: true
        }}>
              {sports.map((sportItem, index) => {
            const href = createSportItemLink(sportItem.id, {
              isMulti: true
            });
            const isActive = !!sportIds?.includes(sportItem.id);
            return <Button key={sportItem.id} {...commonButtonProps} href={href} shallow title={sportItem.title} className={cn(styles.button, styles.buttonIcon, isActive && styles.activeButton)} analytics={getAnalyticsData(href, index, isActive)}>
                    <SportIcon url={sportItem.icon} size={20} isActive={isActive} />
                  </Button>;
          })}
            </Slider>
            <div ref={nextRef} className={styles.navRight}>
              <ButtonIcon as="button" variant="text" size="auto" aria-label="sport toolbar next">
                <ArrowRightIcon width={20} height={20} />
              </ButtonIcon>
            </div>
          </div>
        </div>}

      {!search && <div className={styles.filtersWrapper}>
          <Button {...commonButtonProps} className={cn(styles.button, styles.buttonPregame, {
        [styles.activeButton]: pregameButton.isActive
      })} href={pregameButton.href} shallow color={pregameButton.isActive ? 'red' : 'white'} variant={pregameButton.isActive ? 'filled' : 'text'} skew="right" analytics={getFilterToolbarAnalyticsEvents(pregameButton)}>
            <PregameIcon className={styles.icon} />
            {pregameButton.text}
          </Button>
          <Button {...commonButtonProps} className={cn(styles.button, styles.buttonLive, {
        [styles.activeButton]: liveButton.isActive
      })} href={liveButton.href} shallow color={liveButton.isActive ? 'red' : 'white'} variant={liveButton.isActive ? 'filled' : 'text'} skew="right" analytics={getFilterToolbarAnalyticsEvents(liveButton)}>
            <LiveIcon className={styles.icon} />
            {liveButton.text}
          </Button>
          {!isCyberSportPage && <NoSSR>
              <Button {...commonButtonProps} className={cn(styles.button, styles.buttonLive, {
          [styles.activeButton]: stage === 'OUTRIGHT'
        })} href={createStageLink('OUTRIGHT')} shallow color={stage === 'OUTRIGHT' ? 'red' : 'white'} variant={stage === 'OUTRIGHT' ? 'filled' : 'text'} skew="right">
                <CalendarIcon className={styles.icon} />
                {t('outright', 'Outrights')}
              </Button>
            </NoSSR>}
        </div>}
    </div>;
};